// =============================================================================
// LP Express lead-capture template registry.
//
// Single source of truth for WHICH LP Express template types capture leads via
// the public POST /api/public/lp-express/:brandSlug/:slug/leads endpoint. The
// public route gates submissions on this allow-list (replacing the old hard
// `templateType !== "coffee-shop"` check), and the master-only /admin/leads
// view uses the labels for its template column/filter.
//
// Adding a future lead-capturing template (e.g. temecula-passport) = add its
// key here; the capture endpoint and the admin view pick it up automatically.
// =============================================================================

export const LEAD_CAPTURE_TEMPLATE_TYPES = ["coffee-shop", "temecula-passport"] as const;

export type LeadCaptureTemplateType = (typeof LEAD_CAPTURE_TEMPLATE_TYPES)[number];

export function isLeadCaptureTemplateType(t: unknown): t is LeadCaptureTemplateType {
  return (
    typeof t === "string" &&
    (LEAD_CAPTURE_TEMPLATE_TYPES as readonly string[]).includes(t)
  );
}

// Display labels for the admin leads view. Falls back to the raw key for any
// template captured before its label was added here.
export const LEAD_CAPTURE_TEMPLATE_LABELS: Record<string, string> = {
  "coffee-shop": "Lead Magnet",
  "temecula-passport": "Mystery Drop",
};

export function leadCaptureTemplateLabel(key: string): string {
  return LEAD_CAPTURE_TEMPLATE_LABELS[key] ?? key;
}

// CSV cell encoder for the admin leads export. Lead fields (name/email/phone)
// come from a PUBLIC endpoint and the CSV is opened by privileged admins in
// spreadsheet apps, so beyond standard quoting we neutralize formula
// injection: any cell whose first non-whitespace char is = + - @ (or a
// tab/CR-led variant) gets a leading apostrophe so Excel/Sheets treat it as
// text, never as a formula.
export function csvSafeCell(v: unknown): string {
  let s = v == null ? "" : String(v);
  if (/^[\s\t\r]*[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n\r\t]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
